import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { Nft, NftContract } from '../../models/ProjectDetailModel';
import {
  createNftActionRequest,
  updateNftActionRequest,
} from '../../store/nft/nftActions';
import * as nftActionTypes from '../../store/nft/nftActionTypes';
import './MercurXNftDetailInputs.scss';

function MercurXNftDetailInputs(props) {
  const {
    isEdit,
    createNftRequest,
    updateNftRequest,
    createdNft,
    updatedNft,
    nftErrorType,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const selectedNft = location.state?.nft;
  const user = JSON.parse(localStorage.getItem('user'))?.user;

  const [nftDetail, setNftDetail] = useState(new Nft());
  const [nftContract, setNftContract] = useState(new NftContract());
  const [requiredErrors, setRequiredErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedNft) {
      setNftDetail(new Nft(selectedNft));
      if (selectedNft.nft_contract) {
        setNftContract(new NftContract(selectedNft.nft_contract));
      } else {
        setNftContract(
          new NftContract({
            contract_address: selectedNft.contract_address,
            contract_owner_address: selectedNft.contract_owner_address,
          }),
        );
      }
    }
  }, [selectedNft]);

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }

    if (!isEdit && createdNft) {
      setIsSubmitting(false);
      history.goBack();
      return;
    }

    if (isEdit && updatedNft) {
      setIsSubmitting(false);
      history.goBack();
      return;
    }

    if (!isEdit && nftErrorType === nftActionTypes.CREATE_NFT_ERROR) {
      setIsSubmitting(false);
      return;
    }

    if (isEdit && nftErrorType === nftActionTypes.UPDATE_NFT_ERROR) {
      setIsSubmitting(false);
    }
  }, [createdNft, history, isEdit, isSubmitting, nftErrorType, updatedNft]);

  function clearError(errorKey) {
    setRequiredErrors((prev) => prev.filter((item) => item !== errorKey));
  }

  function nftDetailOnChange(e) {
    const key = e.target.id;
    const value = e.target.value;

    nftDetail[key] = value;
    setNftDetail({ ...nftDetail });
    clearError(`nft_detail_${key}`);
  }

  function nftContractOnChange(e) {
    const key = e.target.id;
    const value = e.target.value;

    nftContract[key] = value;
    setNftContract({ ...nftContract });
    const errorKey =
      key === 'contract_address' ? 'nft_contract_address' : 'nft_contract_owner_address';
    clearError(errorKey);
  }

  function controlInputs(key) {
    return requiredErrors.includes(key);
  }

  function validateNft() {
    const errors = [];

    if (!nftDetail.name) {
      errors.push('nft_detail_name');
    }
    if (!nftDetail.url) {
      errors.push('nft_detail_url');
    }
    if (!nftDetail.address) {
      errors.push('nft_detail_address');
    }
    if (!nftDetail.owner_address) {
      errors.push('nft_detail_owner_address');
    }
    if (nftDetail.total_supply === '' || nftDetail.total_supply === null) {
      errors.push('nft_detail_total_supply');
    }
    if (nftDetail.price_in_usd === '' || nftDetail.price_in_usd === null) {
      errors.push('nft_detail_price_in_usd');
    }
    if (nftDetail.price_in_mercurx === '' || nftDetail.price_in_mercurx === null) {
      errors.push('nft_detail_price_in_mercurx');
    }
    if (nftDetail.distribution === '' || nftDetail.distribution === null) {
      errors.push('nft_detail_distribution');
    }
    if (nftDetail.current_price === '' || nftDetail.current_price === null) {
      errors.push('nft_detail_current_price');
    }
    if (nftDetail.all_time_high === '' || nftDetail.all_time_high === null) {
      errors.push('nft_detail_all_time_high');
    }

    if (!nftContract.contract_address) {
      errors.push('nft_contract_address');
    }

    if (!nftContract.contract_owner_address) {
      errors.push('nft_contract_owner_address');
    }

    setRequiredErrors(errors);
    return errors.length === 0;
  }

  function normalizeNumber(value) {
    if (value === '' || value === null || value === undefined) {
      return 0;
    }
    return Number(value);
  }

  function createNft() {
    if (!validateNft()) {
      return;
    }

    const body = {
      ...nftDetail,
      total_supply: normalizeNumber(nftDetail.total_supply),
      price_in_usd: normalizeNumber(nftDetail.price_in_usd),
      price_in_mercurx: normalizeNumber(nftDetail.price_in_mercurx),
      distribution: normalizeNumber(nftDetail.distribution),
      current_price: normalizeNumber(nftDetail.current_price),
      all_time_high: normalizeNumber(nftDetail.all_time_high),
      nft_contract: {
        ...nftContract,
      },
    };

    const payload = {
      body,
    };

    setIsSubmitting(true);
    createNftRequest(payload);
  }

  function updateNft() {
    if (!validateNft() || !selectedNft?.id) {
      return;
    }

    const body = {
      ...nftDetail,
      total_supply: normalizeNumber(nftDetail.total_supply),
      price_in_usd: normalizeNumber(nftDetail.price_in_usd),
      price_in_mercurx: normalizeNumber(nftDetail.price_in_mercurx),
      distribution: normalizeNumber(nftDetail.distribution),
      current_price: normalizeNumber(nftDetail.current_price),
      all_time_high: normalizeNumber(nftDetail.all_time_high),
      nft_contract: {
        ...nftContract,
      },
    };

    const payload = {
      nftID: selectedNft.id,
      body,
    };

    setIsSubmitting(true);
    updateNftRequest(payload);
  }

  return (
    <div className="nft-detail">
      <Accordion className="mt-5 shadow" defaultActiveKey={['0', '1']} alwaysOpen>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="text-fs-head-sm">NFT</div>
          </Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md="6">
                <Form.Group className="mt-2">
                  <Form.Label>NFT Name*</Form.Label>
                  <Form.Control
                    id="name"
                    type="text"
                    value={nftDetail.name}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_name')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>NFT URL*</Form.Label>
                  <Form.Control
                    id="url"
                    type="text"
                    value={nftDetail.url}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_url')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>NFT Address*</Form.Label>
                  <Form.Control
                    id="address"
                    type="text"
                    value={nftDetail.address}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_address')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>NFT Owner Address*</Form.Label>
                  <Form.Control
                    id="owner_address"
                    type="text"
                    value={nftDetail.owner_address}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_owner_address')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>All Time High*</Form.Label>
                  <Form.Control
                    id="all_time_high"
                    type="number"
                    value={nftDetail.all_time_high}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_all_time_high')}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mt-2">
                  <Form.Label>Total Supply*</Form.Label>
                  <Form.Control
                    id="total_supply"
                    type="number"
                    value={nftDetail.total_supply}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_total_supply')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Price in USD*</Form.Label>
                  <Form.Control
                    id="price_in_usd"
                    type="number"
                    value={nftDetail.price_in_usd}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_price_in_usd')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Price in MERX*</Form.Label>
                  <Form.Control
                    id="price_in_mercurx"
                    type="number"
                    value={nftDetail.price_in_mercurx}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_price_in_mercurx')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Distribution*</Form.Label>
                  <Form.Control
                    id="distribution"
                    type="number"
                    value={nftDetail.distribution}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_distribution')}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Current Price*</Form.Label>
                  <Form.Control
                    id="current_price"
                    type="number"
                    value={nftDetail.current_price}
                    onChange={nftDetailOnChange}
                    isInvalid={controlInputs('nft_detail_current_price')}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <div className="text-fs-head-sm">Contract</div>
          </Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md="6">
                <Form.Group className="mt-2">
                  <Form.Label>NFT Sale Contract Address*</Form.Label>
                  <Form.Control
                    id="contract_address"
                    type="text"
                    value={nftContract.contract_address}
                    onChange={nftContractOnChange}
                    isInvalid={controlInputs('nft_contract_address')}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mt-2">
                  <Form.Label>NFT Sale Contract Owner Address*</Form.Label>
                  <Form.Control
                    id="contract_owner_address"
                    type="text"
                    value={nftContract.contract_owner_address}
                    onChange={nftContractOnChange}
                    isInvalid={controlInputs('nft_contract_owner_address')}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="text-end nft-detail__actions">
        {requiredErrors.length > 0 && (
          <div className="text-danger mt-2">*Required fields must be fill.</div>
        )}

        {user.is_superuser && isEdit && (
          <Button className="mt-2" onClick={updateNft}>
            Update NFT
          </Button>
        )}

        {user.is_superuser && !isEdit && (
          <Button className="mt-2" onClick={createNft}>
            Create NFT
          </Button>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    createdNft: state.nftReducer.createdNft,
    updatedNft: state.nftReducer.updatedNft,
    nftErrorType: state.nftReducer.error?.type,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createNftRequest: (payload) => {
      dispatch(createNftActionRequest(payload));
    },
    updateNftRequest: (payload) => {
      dispatch(updateNftActionRequest(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MercurXNftDetailInputs);
